/**
 * Home's section furniture and its busy state (#12073).
 *
 * The section heading and the card grid are shared by every section Home
 * draws. The placeholders are the shape of what Home is about to show — a
 * row of folder cards, then a table — drawn while its reads are out, and
 * drawn again by the page skeleton that precedes the shell, so the two
 * stages of loading make the same promise. Nothing here reads data, which
 * is what lets the skeleton import it.
 */
import React from "react";
import { rowActivationProps } from "./rowActivation";

// The section heading is one clean step above the rows and one below the
// page heading (#6559 round 15, Claude Design): 15px at 600 — at 13.5px it
// was the rows' own size and weight alone did not carry the distinction, and
// Drive's 16px+ would crowd the 21px page heading with three of these
// stacked. No caps, no tracking (caps are the table's column headers). 24px
// above, 10px below, and nothing above the first: the chips row already sets
// it off.
export const Section: React.FC<{
  title: string;
  /** How many the section holds, after the title (the portal documents'
   *  total, #11635 slice 1, R32), grouped as the chips row groups it;
   *  absent or null while unknown. */
  count?: number | null;
  /** What the heading carries after the count (the sync a service's
   *  documents are narrowed to, Round 15, R50). */
  trailing?: React.ReactNode;
  /** A control at the heading row's right end (the Services band's view
   *  toggle, #11635 Round 17, R63). With one, the row centres its items so
   *  the title sits level with the control, and the title, count and
   *  trailing keep their shared baseline in a group of their own that
   *  wraps under the title on a narrow frame. */
  end?: React.ReactNode;
  /** The section element, for a host that scrolls it into view (R50). */
  sectionRef?: React.Ref<HTMLElement>;
  /** The section's id, for a link elsewhere on the page that leads to it
   *  (#13095 R-R1b: the given-up line to the Sync history). */
  id?: string;
  children: React.ReactNode;
}> = ({
  title,
  count = null,
  trailing = null,
  end = null,
  sectionRef,
  id,
  children,
}) => {
  const heading = (
    <>
      {title}
      {count !== null && (
        <span className="font-mono text-[12.5px] font-normal text-[var(--fp-ink-3)] [font-variant-numeric:tabular-nums]">
          {count.toLocaleString()}
        </span>
      )}
      {trailing}
    </>
  );
  return (
    <section
      ref={sectionRef}
      id={id}
      aria-label={title}
      className="flex flex-col gap-2.5 pt-6 first:pt-0"
    >
      {end === null ? (
        <h2 className="m-0 flex min-h-5 flex-wrap items-baseline gap-x-2 gap-y-1 px-4 text-[15px] font-semibold leading-5 text-[var(--fp-ink)]">
          {heading}
        </h2>
      ) : (
        <div className="flex items-center gap-x-3 px-4">
          <h2 className="m-0 flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-1 text-[15px] font-semibold leading-5 text-[var(--fp-ink)]">
            {heading}
          </h2>
          <div className="flex-none">{end}</div>
        </div>
      )}
      {children}
    </section>
  );
};

/** Home's card: an icon tile, a name and a line of metadata. Recent folders,
 *  Recent records and a portal service's managers (#11635 Round 18) are the
 *  same shape, so they are the same component. A card that opens something
 *  is reached by Tab and opened with Enter or Space, as a row is
 *  (`rowActivationProps`); a card with nowhere to go (no `onOpen`) is plain:
 *  no pointer, no hover, no tab stop. */
export const HomeCard: React.FC<{
  name: string;
  meta: string;
  glyph: React.ReactNode;
  onOpen?: () => void;
}> = ({ name, meta, glyph, onOpen }) => (
  <article
    aria-label={name}
    // A card that opens something is announced as the control it is.
    role={onOpen ? "button" : undefined}
    {...(onOpen ? rowActivationProps(onOpen) : {})}
    className={`flex h-[58px] items-center gap-3 rounded-[10px] border border-[var(--fp-border)] bg-[var(--fp-surface)] pl-3 pr-3 shadow-[var(--fp-lift)] ${
      onOpen
        ? "cursor-pointer outline-none hover:border-[var(--fp-border-strong)] focus-visible:border-[var(--fp-border-strong)] focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--fp-ink-3)]"
        : ""
    }`}
  >
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] bg-[var(--fp-ink-wash)]">
      {glyph}
    </span>
    <span className="flex min-w-0 flex-col">
      <span className="truncate text-[13.5px] font-medium text-[var(--fp-ink)]">
        {name}
      </span>
      <span className="truncate text-[12px] text-[var(--fp-ink-3)]">
        {meta}
      </span>
    </span>
  </article>
);

export const CARD_GRID =
  "grid grid-cols-[repeat(auto-fill,minmax(210px,1fr))] gap-3 px-4";

/** Recent folders is a strip of re-entry points, not coverage: at most four
 *  cards on one row (round 19, Claude Design Q3). */
export const FOLDER_CARDS = 4;

/** The placeholder rows stand in for a table that lists up to twelve; six
 *  is enough to read as a list without promising a full screen. */
const PLACEHOLDER_ROWS = 6;

// A bar where text will be. Still, like the listing's own skeleton rows
// (Claude Design, round 8): nothing spins.
const bar = "block rounded-[4px] bg-[var(--fp-ink-wash)]";

/** Four cards the height and shape of a Home card, with no name yet. */
export const CardPlaceholders: React.FC = () => (
  <div className={CARD_GRID} aria-hidden>
    {Array.from({ length: FOLDER_CARDS }, (_, index) => (
      <div
        key={index}
        data-skeleton="home-card"
        className="flex h-[58px] items-center gap-3 rounded-[10px] border border-[var(--fp-border)] bg-[var(--fp-surface)] px-3"
      >
        <span className="h-9 w-9 shrink-0 rounded-[8px] bg-[var(--fp-ink-wash)]" />
        <span className="flex flex-col gap-1.5">
          <span className={`${bar} h-3.5 w-28`} />
          <span className={`${bar} h-3 w-20`} />
        </span>
      </div>
    ))}
  </div>
);

/** Rows at the listing's own height: at desktop the table's, a name and
 *  two shorter cells under a header line; at phone the record card's, a
 *  glyph beside a name and its line of metadata. */
export const RowPlaceholders: React.FC = () => (
  <div className="px-4" aria-hidden>
    <div className="flex h-9 items-center gap-4 border-b border-[var(--fp-border)] max-md:hidden">
      <span className={`${bar} h-2.5 w-12`} />
      <span className={`${bar} ml-auto h-2.5 w-12`} />
      <span className={`${bar} h-2.5 w-16`} />
    </div>
    {Array.from({ length: PLACEHOLDER_ROWS }, (_, index) => (
      <div
        key={index}
        data-skeleton="home-row"
        className="flex h-[45px] items-center gap-4 border-b border-[var(--fp-border)] max-md:h-[56px] max-md:gap-3"
      >
        <span className="hidden h-4 w-4 shrink-0 rounded-[3px] bg-[var(--fp-ink-wash)] max-md:block" />
        <span className="flex min-w-0 flex-1 items-center gap-4 max-md:flex-col max-md:items-start max-md:gap-1.5">
          <span className={`${bar} h-3.5 w-[46%] max-md:w-[64%]`} />
          <span
            className={`${bar} ml-auto h-3.5 w-[14%] max-md:ml-0 max-md:h-3 max-md:w-[84%]`}
          />
          <span className={`${bar} h-3.5 w-[18%] max-md:hidden`} />
        </span>
      </div>
    ))}
  </div>
);

/** Home while its reads are out: the folder strip and the table, each under
 *  the heading it will keep. */
export const HomePlaceholders: React.FC = () => (
  <>
    <Section title="Recent folders">
      <CardPlaceholders />
    </Section>
    <Section title="Recently added">
      <RowPlaceholders />
    </Section>
  </>
);
